/**
 * InviteAcceptPage - 邀请接受页面
 * 用户点击邀请链接后的落地页
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Brain, Users, Shield, Check, X, 
  Loader2, AlertCircle, LogIn, UserPlus,
  Clock
} from 'lucide-react'
import { 
  getInvitationByToken, 
  acceptInvitation,
  InvitationPublicInfo,
  roleDisplayNames
} from '../services/invitation'
import { useAuthStore } from '../stores/authStore'
import { parseApiError } from '../services/api'

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  
  const [isLoading, setIsLoading] = useState(true)
  const [isAccepting, setIsAccepting] = useState(false)
  const [invitation, setInvitation] = useState<InvitationPublicInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  // 加载邀请信息
  useEffect(() => {
    if (!token) {
      setError('无效的邀请链接')
      setIsLoading(false)
      return
    }
    
    const loadInvitation = async () => {
      try {
        const data = await getInvitationByToken(token)
        setInvitation(data)
      } catch (err) {
        const apiError = parseApiError(err)
        setError(apiError.message)
      } finally {
        setIsLoading(false)
      }
    }
    
    loadInvitation()
  }, [token])
  
  // 接受邀请
  const handleAccept = async () => {
    if (!invitation?.invitationId) return
    
    setIsAccepting(true)
    setError(null)
    
    try {
      const result = await acceptInvitation(invitation.invitationId)
      setSuccess(true)
      
      // 3秒后跳转到 Deal 详情
      setTimeout(() => {
        navigate(`/deal/${result.dealId}`)
      }, 3000)
    } catch (err) {
      const apiError = parseApiError(err)
      setError(apiError.message)
    } finally {
      setIsAccepting(false)
    }
  }
  
  // 格式化过期时间
  const formatExpiresAt = (expiresAt: string) => {
    const date = new Date(expiresAt)
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  // 检查邮箱是否匹配
  const emailMatches = user?.email?.toLowerCase() === invitation?.inviteeEmail?.toLowerCase()
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg shadow-primary-500/30 mb-4"
          >
            <Brain className="w-8 h-8 text-white" />
          </motion.div>
        </div>
        
        {/* 主卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-xl"
        >
          {/* 加载中 */}
          {isLoading && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 animate-spin text-primary-500 mx-auto mb-4" />
              <p className="text-slate-400">正在加载邀请信息...</p>
            </div>
          )}
          
          {/* 邀请无效 */}
          {!isLoading && invitation && !invitation.valid && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <X className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">邀请已失效</h2>
              <p className="text-slate-400 mb-6">
                {invitation.message || '此邀请链接已过期或已被使用'}
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          )}
          
          {/* 邀请有效 */}
          {!isLoading && invitation && invitation.valid && !success && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-primary-500/20 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-primary-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">您收到一份邀请</h2>
                <p className="text-slate-400">
                  加入 <span className="text-white font-medium">{invitation.dealName}</span> 协作
                </p>
              </div>
              
              {/* 邀请详情 */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-400">角色</span>
                  <span className="flex items-center gap-2 text-white">
                    {invitation.role === 'presales' ? (
                      <Users className="w-4 h-4 text-blue-400" />
                    ) : (
                      <Shield className="w-4 h-4 text-purple-400" />
                    )}
                    {roleDisplayNames[invitation.role!]}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-400">权限</span>
                  <span className="text-white text-sm">{invitation.roleDescription}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-400">有效期至</span>
                  <span className="flex items-center gap-1 text-white text-sm">
                    <Clock className="w-4 h-4 text-slate-400" />
                    {formatExpiresAt(invitation.expiresAt!)}
                  </span>
                </div>
              </div>
              
              {/* 未登录 */}
              {!isAuthenticated && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-400 text-center mb-4">
                    请先登录或注册以接受邀请
                  </p>
                  <Link
                    to={`/login?redirect=/invite/${token}`}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
                  >
                    <LogIn className="w-5 h-5" />
                    登录
                  </Link>
                  <Link
                    to={`/register?redirect=/invite/${token}`}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
                  >
                    <UserPlus className="w-5 h-5" />
                    注册新账号
                  </Link>
                </div>
              )}
              
              {/* 已登录 */}
              {isAuthenticated && (
                <div>
                  {/* 邮箱不匹配警告 */}
                  {!emailMatches && (
                    <div className="mb-4">
                      <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium">邮箱不匹配</p>
                          <p className="text-amber-400/80">
                            此邀请发送给 <strong>{invitation.inviteeEmail}</strong>，
                            但您当前登录的邮箱是 <strong>{user?.email}</strong>
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // 退出当前账号，跳转到登录页（带 redirect）
                          useAuthStore.getState().logout()
                          navigate(`/login?redirect=/invite/${token}`)
                        }}
                        className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                      >
                        <LogIn className="w-4 h-4" />
                        切换到 {invitation.inviteeEmail} 登录
                      </button>
                    </div>
                  )}
                  
                  {/* 错误提示 */}
                  {error && (
                    <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}
                  
                  {/* 接受按钮 */}
                  <button
                    onClick={handleAccept}
                    disabled={isAccepting || !emailMatches}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-colors ${
                      emailMatches && !isAccepting
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/30'
                        : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {isAccepting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        接受邀请
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* 接受成功 */}
          {success && (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center"
              >
                <Check className="w-8 h-8 text-green-400" />
              </motion.div>
              <h2 className="text-xl font-bold text-white mb-2">加入成功！</h2>
              <p className="text-slate-400 mb-2">
                您已成功加入 <span className="text-white font-medium">{invitation?.dealName}</span>
              </p>
              <p className="text-sm text-slate-500">
                正在跳转到协作空间...
              </p>
            </div>
          )}
          
          {/* 通用错误 */}
          {!isLoading && error && !invitation && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">加载失败</h2>
              <p className="text-slate-400 mb-6">{error}</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          )}
        </motion.div>
        
        {/* 底部版权 */}
        <p className="mt-8 text-center text-slate-500 text-sm">
          © 2026 ToB Sales AI Assistant
        </p>
      </motion.div>
    </div>
  )
}

